import axios from "axios";
import { API_BASE, getHeaders } from "../config/api";

const API_BASE_URL = `${API_BASE}/api`;

// ── Token helpers ────────────────────────────────────────────────────────

const getAdminToken = () => {
    return sessionStorage.getItem("adminToken");
};

const getSubAdminToken = () => {
    return sessionStorage.getItem("subAdminToken");
};

// ── Super Admin profile ──────────────────────────────────────────────────

export const fetchAdminProfile = async () => {
    try {
        const token = getAdminToken();

        // No admin token → do not call API
        if (!token) {
            return {
                success: false,
                message: "No admin authentication token found",
                status: 401,
            };
        }

        const response = await axios.get(
            `${API_BASE_URL}/super-admin/profile`,
            {
                headers: getHeaders(),
            }
        );

        if (response.data?.success) {
            return {
                success: true,
                data: response.data.data,
                message: response.data.message,
                status: response.status,
            };
        }

        return {
            success: false,
            message:
                response.data?.message ||
                "Failed to fetch admin profile",
            status: response.status,
        };

    } catch (error) {
        if (error.response) {

            // Invalid / expired admin token
            if (error.response.status === 401) {
                sessionStorage.removeItem("adminToken");
                sessionStorage.removeItem("adminData");
                sessionStorage.removeItem("adminAuth");

                return {
                    success: false,
                    message: "Session expired. Please login again.",
                    status: 401,
                };
            }

            return {
                success: false,
                message:
                    error.response.data?.message ||
                    "Failed to fetch admin profile",
                status: error.response.status,
            };
        }

        if (error.request) {
            return {
                success: false,
                message:
                    "Unable to connect to server. Please check your network connection.",
                status: 503,
            };
        }

        return {
            success: false,
            message:
                error.message ||
                "An unexpected error occurred",
            status: 500,
        };
    }
};

// ── Staff / Sub Admin profile ───────────────────────────────────────────

export const fetchStaffProfile = async () => {
    try {
        const token = getSubAdminToken();

        // No sub-admin token → do not call API
        if (!token) {
            return {
                success: false,
                message: "No sub-admin authentication token found",
                status: 401,
            };
        }

        const response = await axios.post(
            `${API_BASE_URL}/superAdmin/staff/getprofile`,
            {},
            {
                headers: getHeaders(),
            }
        );

        if (response.data?.success) {
            const staff = response.data.staff;

            return {
                success: true,
                data: {
                    ...staff,
                    role: staff.roleName,
                    image: staff.image,
                },
                message: response.data.message,
                status: response.status,
            };
        }

        return {
            success: false,
            message:
                response.data?.message ||
                "Failed to fetch staff profile",
            status: response.status,
        };

    } catch (error) {
        if (error.response) {

            // Invalid / expired sub-admin token
            if (error.response.status === 401) {
                sessionStorage.removeItem("subAdminToken");
                sessionStorage.removeItem("subAdminData");
                sessionStorage.removeItem("subAdminAuth");

                return {
                    success: false,
                    message: "Session expired. Please login again.",
                    status: 401,
                };
            }

            return {
                success: false,
                message:
                    error.response.data?.message ||
                    "Failed to fetch staff profile",
                status: error.response.status,
            };
        }

        if (error.request) {
            return {
                success: false,
                message:
                    "Unable to connect to server. Please check your network connection.",
                status: 503,
            };
        }

        return {
            success: false,
            message:
                error.message ||
                "An unexpected error occurred",
            status: 500,
        };
    }
};

// ── Unified profile helper ──────────────────────────────────────────────
// Token is the source of truth.
// No token = no API call.

export const fetchProfile = async () => {
    const adminToken = getAdminToken();
    const subAdminToken = getSubAdminToken();

    // Sub Admin token
    if (subAdminToken) {
        return await fetchStaffProfile();
    }

    // Super Admin token
    if (adminToken) {
        return await fetchAdminProfile();
    }

    // No token
    return {
        success: false,
        message: "No authentication token found",
        status: 401,
    };
};